/**
 * Typed wrapper for the agent's Apex connection-config surface:
 *
 *   GET    /api/apex/config  → current connection (password never echoed)
 *   PUT    /api/apex/config  → save + hot-restart the poller
 *   DELETE /api/apex/config  → disconnect
 *   GET    /api/apex/status  → live connection status for the badge
 *   POST   /api/apex/test    → one-shot probe without touching the poller
 *
 * Every call returns a discriminated `Result<T>` rather than throwing so
 * the Settings UI can render network-loss states without try/catch noise.
 */
import { agentFetch } from './agent-fetch';
import { loadPublicEnv } from './env';

export type Result<T> = { ok: true; value: T } | { ok: false; error: string };

export type ApexConnectionState =
  | 'disabled'
  | 'connecting'
  | 'connected'
  | 'auth-failed'
  | 'unreachable';

export interface ApexStatus {
  configured: boolean;
  state: ApexConnectionState;
  host: string | null;
  pollIntervalMs: number | null;
  lastSuccessAtMs: number | null;
  lastError: string | null;
  probeCount: number;
  outletCount: number;
}

export interface ApexConfigView {
  host: string;
  username: string;
  pollIntervalMs: number;
  hasPassword: boolean;
  updatedAtMs: number;
}

export interface ApexConfigInput {
  host: string;
  username: string;
  /** Omit to keep the password already stored on the Pi. */
  password?: string;
  pollIntervalMs?: number;
}

export interface ApexTestResult {
  result: 'connected' | 'auth-failed' | 'unreachable';
  probeCount: number;
  outletCount: number;
  error: string | null;
}

function baseUrl(): string {
  return loadPublicEnv().agentDevUrl.replace(/\/+$/, '');
}

async function request<T>(path: string, init?: RequestInit): Promise<Result<T>> {
  try {
    const res = await agentFetch(`${baseUrl()}${path}`, {
      ...init,
      headers: {
        'content-type': 'application/json',
        ...(init?.headers ?? {}),
      },
    });
    if (!res.ok) {
      return { ok: false, error: `HTTP ${res.status} ${res.statusText}` };
    }
    const value = (await res.json()) as T;
    return { ok: true, value };
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : String(err) };
  }
}

export const apexConfigApi = {
  getConfig(): Promise<Result<{ ok: boolean; config: ApexConfigView | null }>> {
    return request('/api/apex/config');
  },
  putConfig(input: ApexConfigInput): Promise<Result<{ ok: boolean; status: ApexStatus }>> {
    return request('/api/apex/config', { method: 'PUT', body: JSON.stringify(input) });
  },
  deleteConfig(): Promise<Result<{ ok: boolean; status: ApexStatus }>> {
    return request('/api/apex/config', { method: 'DELETE' });
  },
  getStatus(): Promise<Result<{ ok: boolean; status: ApexStatus }>> {
    return request('/api/apex/status');
  },
  test(input: ApexConfigInput): Promise<Result<{ ok: boolean; test: ApexTestResult }>> {
    return request('/api/apex/test', { method: 'POST', body: JSON.stringify(input) });
  },
};

export type ApexConfigApi = typeof apexConfigApi;
